import { Box, Card, CardContent, LinearProgress } from "@mui/material";
import React from "react";
import { useTranslation } from "react-i18next";
import { useNavigate, useParams, useSearchParams } from "react-router-dom";
import SimpleAlert from "../../../../components/display/SimpleAlert";
import PageContentWrapper from "../../../../components/wrappers/PageContentWrapper";
import { PETS_GENERAL } from "../../../../enums/QueryKeys";
import { PET_DETAILS_, PET_VIEW_USER_PARAM } from "../../../../enums/UrlPaths";
import { useFetch } from "../../../../hooks/Fetching";
import { GENERAL } from "../../../pets/enums/TabsRequestParams";
import TabPanel from "../../components/core/TabPanel";
import NotFound, { ErrorMessage } from "../../components/display/NotFound";
import PetHeader from "./components/petView/PetHeader";
import PetViewTabs from "./components/petView/PetViewTabs";

// Re-exported for the saps pages that already import it from here
export { TabPanel };

function isForbidden(error) {
  return String(error?.response?.data).toUpperCase() === "FORBIDDEN";
}

// Single pet view shared by users, clinics and master - served on both
// PET_DETAILS_ID (/pets/pet/:id?user=) and SINGLE_PET_VIEW (/saps/pets/pet/:id)
const SinglePetView = () => {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const { id } = useParams();
  const url = id ? PET_DETAILS_ + id + "?tab=" + GENERAL : null;
  const { data, isLoading, error } = useFetch(
    url,
    null,
    [PETS_GENERAL + id],
    true,
    0,
  );

  React.useEffect(() => {
    if (isForbidden(error)) navigate(-1);
  }, [error, navigate]);

  // Keep ?user= in sync with the pet's owner, e.g. after the owner is changed in the edit dialog
  const [searchParams, setSearchParams] = useSearchParams();
  const urlUserId = searchParams.get(PET_VIEW_USER_PARAM);
  const ownerId = data?.userId != null ? String(data.userId) : null;
  React.useEffect(() => {
    if (!data || urlUserId === ownerId) return;
    setSearchParams(
      (params) => {
        if (ownerId) params.set(PET_VIEW_USER_PARAM, ownerId);
        else params.delete(PET_VIEW_USER_PARAM);
        return params;
      },
      { replace: true },
    );
  }, [data, ownerId, urlUserId, setSearchParams]);

  if (isLoading) {
    return (
      <PageContentWrapper>
        <LinearProgress />
      </PageContentWrapper>
    );
  } else if (error) {
    if (isForbidden(error)) return null;
    return (
      <PageContentWrapper>
        <ErrorMessage
          title={t("ErrorFetchingPetData")}
          subtitle={t("SomethingWentWrong")}
        />
      </PageContentWrapper>
    );
  } else if (!data) {
    return (
      <NotFound title={t("PetNotFound")} subtitle={t("PetNotFoundSubtitle")} />
    );
  }

  return (
    <PageContentWrapper maxWidth="xl">
      {/* minmax(0, 1fr) stops wide content (the tab rows) from stretching the page on
          tablets; the page sits in a flex layout, so without it the tabs never scroll */}
      <Box sx={{ display: "grid", gridTemplateColumns: "minmax(0, 1fr)" }}>
        <SimpleAlert
          show={data?.active === false}
          severity="warning"
          message="InactivePetAlert"
          mb={2}
          testId="pet.view.inactive-alert"
        />
        <Header pet={data} />
        <PetViewTabs pet={data} />
      </Box>
    </PageContentWrapper>
  );
};

function Header({ pet }) {
  return (
    <Card
      sx={{
        mb: 3,
        mx: "auto",
        width: "100%",
        maxWidth: "100%",
        overflow: "hidden",
      }}
    >
      <CardContent sx={{ p: { xs: 2, sm: 4 } }}>
        <PetHeader pet={pet} />
      </CardContent>
    </Card>
  );
}

export default SinglePetView;
